import { translate } from "@/lib/translation";
import { API_BASE_URL, authorizedFetch } from "./base";
import {
  Transaction,
  TransactionQueryParams,
  PaginatedResponse,
  RawTransactionResponse,
  TransactionType,
  ListingStatus,
  ManualTransactionCreate,
  UploadTransactionsResponse,
  EditedTransactionPayload,
  SaveEditedTransactionsResponse,
  TransactionSelection,
  ClarificationResponse,
  UploadProgressMap,
} from "./types";
import { parseDecimal, normalizeFiat } from "./utils";

// TECH-DEBT: duplication — the RawTransactionResponse→Transaction mapping below is
// copy-pasted verbatim in both getTransactionsPaginated and getTransactions.
// Extract to a private mapTransaction(tx: RawTransactionResponse): Transaction helper.
export const getTransactionsPaginated = async (
  portfolioId: number,
  params: TransactionQueryParams,
): Promise<PaginatedResponse<Transaction>> => {
  const query = new URLSearchParams();
  if (params.page != null) query.append("page", params.page.toString());
  if (params.size != null) query.append("size", params.size.toString());
  if (params.sort) query.append("sort", params.sort);
  if (params.dir) query.append("dir", params.dir);
  if (params.ticker) query.append("ticker", params.ticker);
  if (params.date_from) query.append("date_from", params.date_from);
  if (params.date_to) query.append("date_to", params.date_to);
  if (params.types && params.types.length > 0) {
    params.types.forEach((t) => query.append("types", t));
  }

  const rawData = await authorizedFetch<
    PaginatedResponse<RawTransactionResponse>
  >(`/portfolios/${portfolioId}/transactions/query?${query.toString()}`);

  return {
    ...rawData,
    items: rawData.items.map((tx) => ({
      id: tx.id,
      portfolio_id: tx.portfolio_id,
      date: tx.date,
      type: tx.type as TransactionType,
      listing_id: tx.listing_id ?? null,
      listing: tx.listing
        ? {
            id: tx.listing.id,
            provider: tx.listing.provider,
            provider_symbol: tx.listing.provider_symbol ?? null,
            ticker: tx.listing.ticker,
            exchange: tx.listing.exchange,
            currency: tx.listing.currency,
            isin: tx.listing.isin ?? null,
            company_name: tx.listing.company_name ?? null,
            status: (tx.listing.status ?? "ACTIVE") as ListingStatus,
            delisted_at: tx.listing.delisted_at ?? null,
          }
        : null,
      ticker: tx.ticker ?? null,
      quantity: parseDecimal(tx.quantity),
      price_native: parseDecimal(tx.price_native),
      price_in_listing_currency: parseDecimal(tx.price_in_listing_currency),
      fee_native: parseDecimal(tx.fee_native),
      fee_currency: normalizeFiat(tx.fee_currency),
      currency_native: normalizeFiat(tx.currency_native),
      base_to_native_fx_rate: parseDecimal(tx.base_to_native_fx_rate),
      from_currency: normalizeFiat(tx.from_currency),
      from_amount: parseDecimal(tx.from_amount),
      to_currency: normalizeFiat(tx.to_currency),
      to_amount: parseDecimal(tx.to_amount),
      withholding_tax_native: parseDecimal(tx.withholding_tax_native),
      isin: tx.isin ?? null,
      settlement_amount: parseDecimal(tx.settlement_amount),
      settlement_currency: normalizeFiat(tx.settlement_currency),
      notes: tx.notes ?? null,
      total_value: parseDecimal(tx.total_value),
    })),
  };
};

export async function getTransactions(
  portfolioId: number,
): Promise<Transaction[]> {
  const rawData = await authorizedFetch<RawTransactionResponse[]>(
    `/portfolios/${portfolioId}/transactions/`,
  );
  if (!Array.isArray(rawData)) return [];

  return rawData.map((tx) => ({
    id: tx.id,
    portfolio_id: tx.portfolio_id,
    date: tx.date,
    type: tx.type as TransactionType,
    listing_id: tx.listing_id ?? null,
    listing: tx.listing
      ? {
          id: tx.listing.id,
          provider: tx.listing.provider,
          provider_symbol: tx.listing.provider_symbol ?? null,
          ticker: tx.listing.ticker,
          exchange: tx.listing.exchange,
          currency: tx.listing.currency,
          isin: tx.listing.isin ?? null,
          company_name: tx.listing.company_name ?? null,
          status: (tx.listing.status ?? "ACTIVE") as ListingStatus,
          delisted_at: tx.listing.delisted_at ?? null,
        }
      : null,
    ticker: tx.ticker ?? null,
    quantity: parseDecimal(tx.quantity),
    price_native: parseDecimal(tx.price_native),
    price_in_listing_currency: parseDecimal(tx.price_in_listing_currency),
    fee_native: parseDecimal(tx.fee_native),
    fee_currency: normalizeFiat(tx.fee_currency),
    currency_native: normalizeFiat(tx.currency_native),
    base_to_native_fx_rate: parseDecimal(tx.base_to_native_fx_rate),
    from_currency: normalizeFiat(tx.from_currency),
    from_amount: parseDecimal(tx.from_amount),
    to_currency: normalizeFiat(tx.to_currency),
    to_amount: parseDecimal(tx.to_amount),
    withholding_tax_native: parseDecimal(tx.withholding_tax_native),
    isin: tx.isin ?? null,
    settlement_amount: parseDecimal(tx.settlement_amount),
    settlement_currency: normalizeFiat(tx.settlement_currency),
    notes: tx.notes ?? null,
  }));
}

export async function createTransaction(
  portfolioId: number,
  payload: ManualTransactionCreate,
) {
  return authorizedFetch(`/portfolios/${portfolioId}/transactions/`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function deleteTransactions(
  transactionIds: number[],
  portfolioId: number,
) {
  return authorizedFetch(`/portfolios/${portfolioId}/transactions/delete`, {
    method: "POST",
    body: JSON.stringify({
      transaction_ids: transactionIds,
    }),
  });
}

export async function uploadTransactions(
  files: File[],
  portfolioId: number,
  uploadId?: string,
  preview: boolean = false,
  skipDuplicates: boolean = true,
): Promise<UploadTransactionsResponse> {
  if (files.length === 0) {
    throw new Error("No files provided for upload");
  }
  const formData = new FormData();
  files.forEach((file) => {
    formData.append("files", file);
  });
  formData.append("portfolio_id", portfolioId.toString());
  if (uploadId) {
    formData.append("upload_id", uploadId);
  }

  formData.append("preview", preview.toString());
  formData.append("skip_duplicates", skipDuplicates.toString());

  return authorizedFetch<UploadTransactionsResponse>(
    `/users/me/transactions/upload`,
    {
      method: "POST",
      body: formData,
    },
  );
}

export async function downloadStandardTemplate(): Promise<Blob> {
  const response = await fetch(
    `${API_BASE_URL}/transactions/standard-template?ts=${Date.now()}`,
    { credentials: "include" },
  );

  if (!response.ok) {
    throw new Error(translate("import:templateDownload.failed"));
  }

  return response.blob();
}

export async function saveEditedTransactions(
  portfolioId: number,
  editedTransactions: EditedTransactionPayload[],
): Promise<SaveEditedTransactionsResponse> {
  return authorizedFetch<SaveEditedTransactionsResponse>(
    `/portfolios/${portfolioId}/transactions/edited`,
    {
      method: "POST",
      body: JSON.stringify(editedTransactions),
    },
  );
}

export async function clarifyAmbiguousTransactions(
  portfolioId: number,
  selections: TransactionSelection[],
): Promise<ClarificationResponse> {
  return authorizedFetch(`/portfolios/${portfolioId}/transactions/clarify`, {
    method: "POST",
    body: JSON.stringify({
      portfolio_id: portfolioId,
      selections,
    }),
  });
}

export async function getUploadProgress(
  uploadId: string,
): Promise<UploadProgressMap> {
  return authorizedFetch<UploadProgressMap>(
    `/users/me/transactions/upload/progress/${uploadId}`,
  );
}

export async function syncT212Portfolio(
  portfolioId: number,
  fullHistory: boolean = false,
): Promise<UploadTransactionsResponse> {
  const query = fullHistory ? "?full_history=true" : "";
  return authorizedFetch<UploadTransactionsResponse>(
    `/users/me/portfolios/${portfolioId}/sync-t212${query}`,
    {
      method: "POST",
    },
  );
}

// TODO(ib-api-sync): the backend endpoint does not exist yet. Step 4 of
// tasks/todo-ib-api-sync.md replaces both of these with a single brokerage-agnostic
// POST /users/me/portfolios/{id}/sync.
export async function syncIBPortfolio(
  portfolioId: number,
  fullHistory: boolean = false,
): Promise<UploadTransactionsResponse> {
  const query = fullHistory ? "?full_history=true" : "";
  return authorizedFetch<UploadTransactionsResponse>(
    `/users/me/portfolios/${portfolioId}/sync-ib${query}`,
    {
      method: "POST",
    },
  );
}
